import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { axiosInstance } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { eventSpansMidnight } from "@/lib/eventTime";
import { TIMEZONE_GROUPS } from "@/pages/CreateEvent";

const FIELD_CLASS =
  "h-11 w-full rounded-[10px] border-[#2a2a2a] bg-[#0f0f0f] px-3.5 text-[15px] text-white placeholder:text-[#5b6270] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const LABEL_CLASS = "mb-2 block text-[13px] font-semibold text-[#c9cfdb]";

const EditEvent = ({ user, onLogout }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    start_time: "",
    end_time: "",
    timezone: "America/New_York",
    image_url: "",
  });

  useEffect(() => {
    let cancelled = false;

    const loadEvent = async () => {
      try {
        const { data } = await axiosInstance.get(`/events/${eventId}`);
        if (cancelled) return;
        if (data.status !== "upcoming") {
          toast.error("Only upcoming events can be edited");
          navigate("/creator/dashboard", { replace: true });
          return;
        }
        setFormData({
          title: data.title || "",
          description: data.description || "",
          date: data.date || "",
          start_time: data.start_time || "",
          end_time: data.end_time || "",
          timezone: data.timezone || "America/New_York",
          image_url: data.image_url || "",
        });
        setImagePreview(data.image_url || "");
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.detail || "Unable to load this event");
          navigate("/creator/dashboard", { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEvent();
    return () => {
      cancelled = true;
    };
  }, [eventId, navigate]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error("Enter an event title and description");
      return;
    }
    if (!formData.date || !formData.start_time || !formData.end_time) {
      toast.error("Choose the event date, start time, and end time");
      return;
    }
    if (formData.start_time === formData.end_time) {
      toast.error("Start and end time cannot be the same");
      return;
    }

    setSaving(true);
    try {
      let imageUrl = formData.image_url;
      if (imageFile) {
        const uploadData = new FormData();
        uploadData.append("file", imageFile);
        const { data } = await axiosInstance.post("/events/upload-image", uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imageUrl = data.image_url.startsWith("http")
          ? data.image_url
          : `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${data.image_url}`;
      }

      await axiosInstance.put(`/events/creator/${eventId}`, {
        ...formData,
        title: formData.title.trim(),
        description: formData.description.trim(),
        image_url: imageUrl,
      });
      toast.success("Event updated successfully");
      navigate("/creator/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update this event");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f0f0f] text-white">
        <Loader2 className="h-7 w-7 animate-spin text-blue-400" aria-label="Loading event" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f]" data-testid="edit-event-page">
      <Navbar user={user} onLogout={onLogout} isCreator={true} />
      <main className="mx-auto w-full max-w-[808px] px-4 pb-16 pt-24 sm:px-6 lg:pt-28">
        <button
          type="button"
          onClick={() => navigate("/creator/dashboard")}
          className="mb-5 flex items-center gap-2 text-sm font-semibold text-[#9aa1af] hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Creator Dashboard
        </button>

        <div className="mb-7">
          <h1 className="text-3xl font-black text-white">Edit future event</h1>
          <p className="mt-2 text-sm text-[#8b93a7]">
            Update event information and scheduling. Tickets, pricing, Pro Mode, payments, and payouts stay unchanged.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="space-y-5 rounded-[18px] border border-[#262626] bg-[#161616] p-5 sm:p-8">
            <div>
              <label className={LABEL_CLASS} htmlFor="edit-event-title">Event title</label>
              <Input
                id="edit-event-title"
                value={formData.title}
                onChange={(event) => setFormData((current) => ({ ...current, title: event.target.value }))}
                className={FIELD_CLASS}
                data-testid="edit-title-input"
              />
            </div>

            <div>
              <label className={LABEL_CLASS} htmlFor="edit-event-description">Description</label>
              <Textarea
                id="edit-event-description"
                value={formData.description}
                onChange={(event) => setFormData((current) => ({ ...current, description: event.target.value }))}
                rows={4}
                className="min-h-[100px] w-full resize-y rounded-[10px] border-[#2a2a2a] bg-[#0f0f0f] px-3.5 py-3 text-[15px] text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                data-testid="edit-description-input"
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>Event poster</label>
              <label className="block cursor-pointer overflow-hidden rounded-xl border-2 border-dashed border-[#333] hover:border-blue-500/70">
                {imagePreview ? (
                  <div className="bg-[#0f0f0f] p-3">
                    <img src={imagePreview} alt="Event poster preview" className="mx-auto max-h-64 rounded-lg object-contain" />
                    <p className="mt-2 text-center text-xs font-medium text-[#9aa1af]">Tap to replace this image</p>
                  </div>
                ) : (
                  <div className="px-5 py-7 text-center">
                    <Upload className="mx-auto mb-2 h-7 w-7 text-blue-400" />
                    <p className="text-sm font-semibold text-[#c9cfdb]">Choose an image</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                  data-testid="edit-image-input"
                />
              </label>
            </div>
          </section>

          <section className="rounded-[18px] border border-[#262626] bg-[#161616] p-5 sm:p-8">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/15 text-blue-400">
                <CalendarDays className="h-[22px] w-[22px]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Date and time</h2>
                <p className="text-xs text-[#8b93a7]">Times use the event time zone selected below.</p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={LABEL_CLASS} htmlFor="edit-event-date">Date</label>
                <Input
                  id="edit-event-date"
                  type="date"
                  value={formData.date}
                  onChange={(event) => setFormData((current) => ({ ...current, date: event.target.value }))}
                  className={`${FIELD_CLASS} showme-blue-picker`}
                  data-testid="edit-date-input"
                />
              </div>
              <div>
                <label className={LABEL_CLASS} htmlFor="edit-event-timezone">Time zone</label>
                <select
                  id="edit-event-timezone"
                  value={formData.timezone}
                  onChange={(event) => setFormData((current) => ({ ...current, timezone: event.target.value }))}
                  className={`${FIELD_CLASS} appearance-auto`}
                  data-testid="edit-timezone-select"
                >
                  {TIMEZONE_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.zones.map((zone) => (
                        <option key={zone.value} value={zone.value}>{zone.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL_CLASS} htmlFor="edit-event-start">Start time</label>
                <Input
                  id="edit-event-start"
                  type="time"
                  value={formData.start_time}
                  onChange={(event) => setFormData((current) => ({ ...current, start_time: event.target.value }))}
                  className={`${FIELD_CLASS} showme-blue-picker`}
                  data-testid="edit-start-time-input"
                />
              </div>
              <div>
                <label className={LABEL_CLASS} htmlFor="edit-event-end">End time</label>
                <Input
                  id="edit-event-end"
                  type="time"
                  value={formData.end_time}
                  onChange={(event) => setFormData((current) => ({ ...current, end_time: event.target.value }))}
                  className={`${FIELD_CLASS} showme-blue-picker`}
                  data-testid="edit-end-time-input"
                />
                {eventSpansMidnight(formData.start_time, formData.end_time) && (
                  <p className="mt-2 text-xs font-medium text-blue-400">Ends the following day</p>
                )}
              </div>
            </div>
          </section>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              onClick={() => navigate("/creator/dashboard")}
              className="h-11 border border-[#333] bg-transparent px-6 text-[#c9cfdb] hover:bg-white/5 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="h-11 min-w-[150px] bg-blue-500 px-6 font-bold text-white hover:bg-blue-400 disabled:opacity-60"
              data-testid="save-event-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default EditEvent;
